import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ReconciliationService } from "@/api";
import type {
  reconciliation_ImportBatchListResponse,
  reconciliation_ImportBatchResponse,
} from "@/api";
import { invalidateMoneyViews } from "./invalidateMoneyViews";

export interface ImportBatchesListFilters {
  limit?: number;
  offset?: number;
}

export const importBatchKeys = {
  all: ["reconciliation", "imports"] as const,
  list: (filters: ImportBatchesListFilters) =>
    [...importBatchKeys.all, "list", filters] as const,
  detail: (id: string) => [...importBatchKeys.all, "detail", id] as const,
};

export function useImportBatchesList(filters: ImportBatchesListFilters = {}) {
  return useQuery<reconciliation_ImportBatchListResponse>({
    queryKey: importBatchKeys.list(filters),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationImports(
        filters.limit,
        filters.offset,
      ),
    placeholderData: keepPreviousData,
  });
}

// A CSV upload auto-applies matched payments and a revert undoes them, so every
// money-related surface may change — not just the batch list. Invalidate the
// review queue (new/removed Active items + badge count), debtor accounts
// (balances / unapplied credit), the per-debtor and global payment registers,
// the per-invoice payment-applications side-sheets, the subsidy matching tab
// (routed office payments), and the Invoices list/detail (derived paid-status).
async function invalidateImportScopes(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  // An import creates payments and a revert deletes them, which touches every
  // money surface there is. This used to hand-pick eight namespaces; the shared
  // list is the same set plus the ones that were missing (the debtor ledger and
  // corrections), and it broadcasts to the operator's other browser tabs.
  await invalidateMoneyViews(queryClient);
}

export function useUploadImport() {
  const queryClient = useQueryClient();
  return useMutation<reconciliation_ImportBatchResponse, unknown, File>({
    mutationFn: (file) =>
      ReconciliationService.postApiV1ReconciliationImports(file),
    onSuccess: async () => invalidateImportScopes(queryClient),
  });
}

export function useRevertImport() {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, string>({
    mutationFn: (id) =>
      ReconciliationService.postApiV1ReconciliationImportsRevert(id),
    onSuccess: async () => invalidateImportScopes(queryClient),
  });
}
